import type { EChartsOption } from "echarts";
import ReactECharts from "echarts-for-react";
import type {
	InspectionDashboardBucket,
	InspectionDashboardGrain,
} from "../../../models/InspectionDashboard";
import { formatCount, formatPercent, rate } from "../../utils/dashboardFormat";
import { DASHBOARD_INK } from "../../utils/dashboardTokens";
import { getInspectionDashboardBucketLabel } from "../../utils/period";

export const BUCKET_NAMES: Record<InspectionDashboardGrain, string> = {
	shift: "by shift",
	day: "by day",
	month: "by month",
};

const gapSeries = (name: string, rates: (number | null)[], color: string) => ({
	name,
	type: "line" as const,
	data: rates,
	color,
	lineStyle: { width: 1.5, type: "dashed" as const, opacity: 0.45 },
	symbol: "none" as const,
	connectNulls: true,
	silent: true,
	z: 1,
});

type TooltipEntry = {
	seriesIndex?: unknown;
	seriesName?: unknown;
	value?: unknown;
	color?: unknown;
	dataIndex?: unknown;
	axisValueLabel?: unknown;
};

type Props = {
	buckets: InspectionDashboardBucket[];
	grain: InspectionDashboardGrain;
	isEvent: boolean;
	periodLabel: string;
};

export const FindingRateChart = ({
	buckets,
	grain,
	isEvent,
	periodLabel,
}: Props) => {
	const labels = buckets.map((bucket) =>
		getInspectionDashboardBucketLabel(bucket, grain, isEvent),
	);

	const defectRates = buckets.map((bucket) =>
		rate(bucket.ReadingsWithDefect, bucket.Readings),
	);
	const noGoRates = buckets.map((bucket) =>
		rate(bucket.ReadingsWithNoGo, bucket.Readings),
	);

	const renderTooltip = (parameters: unknown): string => {
		const entries = Array.isArray(parameters)
			? (parameters as TooltipEntry[])
			: [];
		if (entries.length === 0) return "";

		const index =
			typeof entries[0].dataIndex === "number" ? entries[0].dataIndex : 0;
		const bucket = buckets[index];
		const header =
			typeof entries[0].axisValueLabel === "string"
				? entries[0].axisValueLabel
				: "";

		const rows = entries
			.filter(
				(entry) =>
					typeof entry.seriesIndex !== "number" || entry.seriesIndex >= 2,
			)
			.map((entry) => {
				const color =
					typeof entry.color === "string" ? entry.color : "transparent";
				const value = typeof entry.value === "number" ? entry.value : null;

				return `<div style="display:flex;align-items:center;gap:6px;margin-top:4px">
					<span style="width:8px;height:8px;border-radius:50%;background:${color}"></span>
					<span style="color:${DASHBOARD_INK.SECONDARY}">${entry.seriesName}</span>
					<span style="margin-left:auto;font-weight:600;font-variant-numeric:tabular-nums">${formatPercent(value)}</span>
				</div>`;
			})
			.join("");

		return `<div style="min-width:190px">
			<div style="font-weight:600">${header}</div>
			<div style="color:${DASHBOARD_INK.MUTED};font-size:11px">${formatCount(bucket?.Readings ?? 0)} readings</div>
			${rows}
		</div>`;
	};

	const option: EChartsOption = {
		grid: { top: 28, right: 40, bottom: 35, left: 40, containLabel: true },
		legend: {
			bottom: 0,
			data: ["At least one defect", "At least one no-go"],
			icon: "roundRect",
			itemHeight: 3,
			itemWidth: 14,
			textStyle: { color: DASHBOARD_INK.SECONDARY, fontSize: 12 },
		},
		tooltip: {
			trigger: "axis",
			appendTo: "body",
			axisPointer: { type: "line", lineStyle: { color: DASHBOARD_INK.AXIS } },
			borderColor: DASHBOARD_INK.GRID,
			formatter: renderTooltip,
		},
		xAxis: {
			type: "category",
			data: labels,
			boundaryGap: false,
			axisLine: { lineStyle: { color: DASHBOARD_INK.AXIS } },
			axisTick: { show: false },
			axisLabel: {
				color: DASHBOARD_INK.MUTED,
				fontSize: 11,
				hideOverlap: true,
			},
		},
		yAxis: {
			type: "value",
			name: "% of readings",
			nameGap: 12,
			nameTextStyle: {
				align: "left",
				color: DASHBOARD_INK.MUTED,
				fontSize: 11,
			},
			min: 0,
			axisLabel: {
				color: DASHBOARD_INK.MUTED,
				fontSize: 11,
				formatter: (value: number) => `${value}%`,
			},
			splitLine: { lineStyle: { color: DASHBOARD_INK.GRID } },
		},
		series: [
			gapSeries("At least one defect", defectRates, "#eb6834"),
			gapSeries("At least one no-go", noGoRates, "#2a78d6"),
			{
				name: "At least one defect",
				type: "line",
				data: defectRates,
				color: "#eb6834",
				lineStyle: { width: 2 },
				symbolSize: 8,
				itemStyle: { borderColor: "#ffffff", borderWidth: 2 },
				connectNulls: false,
				z: 3,
			},
			{
				name: "At least one no-go",
				type: "line",
				data: noGoRates,
				color: "#2a78d6",
				lineStyle: { width: 2 },
				symbolSize: 8,
				itemStyle: { borderColor: "#ffffff", borderWidth: 2 },
				connectNulls: false,
				z: 3,
			},
		],
	};

	return (
		<ReactECharts
			option={option}
			notMerge
			style={{ height: 210, width: "100%" }}
			opts={{ renderer: "svg" }}
		/>
	);
};
